import { useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useActivateRegistration } from '~/hooks/registration/useRegistrationStore';
import { usePrimaryRowComparison } from '~/hooks/viewer/usePrimaryRowComparison';
import {
    deformationUncertaintyEnabledState,
    deformationUncertaintySourcesState,
} from '~/state/atoms/deformationDisplayAtoms';
import {
    IsroResult,
    IsroSample,
    isroResultState,
    isroSelectedSampleNumberState,
} from '~/state/atoms/isroResultAtoms';
import { findImageBySeriesUid } from '~/hooks/workflows/findImageBySeriesUid';

export interface IsroComparison {
    readonly result: IsroResult | null;
    readonly selectedNumber: number | null;
    readonly referenceOnScreen: boolean;
    readonly hasDeformation: boolean;
    readonly otherComparisonOpen: boolean;
    readonly select: (sample: IsroSample) => void;
    readonly close: () => void;
}

export function useIsroComparison(): IsroComparison {
    const result = useRecoilValue(isroResultState);
    const [selectedNumber, setSelectedNumber] = useRecoilState(
        isroSelectedSampleNumberState
    );
    const patient = useSelectedPatient();
    const primaryImage = useSelectedPrimaryImage();
    const comparison = usePrimaryRowComparison();
    const activateRegistration = useActivateRegistration();
    const setUncertaintySources = useSetRecoilState(
        deformationUncertaintySourcesState
    );
    const setUncertaintyEnabled = useSetRecoilState(
        deformationUncertaintyEnabledState
    );

    const referenceOnScreen =
        !!result &&
        !!primaryImage &&
        primaryImage.seriesUid === result.referenceSeriesUid;
    const paired = comparison.pairedImage;
    const showsSample =
        !!result &&
        !!paired &&
        result.samples.some((sample) => sample.seriesUid === paired.seriesUid);
    const otherComparisonOpen = !!paired && comparison.apart && !showsSample;
    const hasDeformation =
        !!result &&
        result.samples.every(
            (sample) => sample.registrationSopInstanceUid !== undefined
        );

    useEffect(() => {
        setUncertaintySources(
            hasDeformation && result
                ? result.samples.map(
                      (sample) => sample.registrationSopInstanceUid!
                  )
                : []
        );
    }, [result, hasDeformation, setUncertaintySources]);

    const select = useCallback(
        (sample: IsroSample) => {
            const image = findImageBySeriesUid(patient, sample.seriesUid);
            if (
                !patient ||
                !primaryImage ||
                !image ||
                !referenceOnScreen ||
                image.frameOfReferenceUid !== primaryImage.frameOfReferenceUid
            ) {
                return;
            }
            // Same frame: the pair needs no registration, and its panes share one Environment.
            if (!comparison.show(image, undefined, 'apart')) {
                return;
            }
            if (sample.registrationSopInstanceUid) {
                activateRegistration(
                    sample.registrationSopInstanceUid,
                    image.seriesUid
                );
            }
            setSelectedNumber(sample.number);
        },
        [
            patient,
            primaryImage,
            referenceOnScreen,
            comparison,
            activateRegistration,
            setSelectedNumber,
        ]
    );

    const close = useCallback(() => {
        // Another surface's side-by-side is not this panel's to take down.
        if (showsSample && paired) {
            comparison.end(paired);
        }
        setUncertaintyEnabled(false);
        setSelectedNumber(null);
    }, [
        showsSample,
        paired,
        comparison,
        setUncertaintyEnabled,
        setSelectedNumber,
    ]);

    return {
        result,
        selectedNumber,
        referenceOnScreen,
        hasDeformation,
        otherComparisonOpen,
        select,
        close,
    };
}
